import { useMemo } from "react";
import { useApi } from "src/hooks/useApi";
import { Invoice, Recipient } from "src/types";
import { InvoiceStatus } from "src/constants/invoice";

/** Invoices no longer "in" any dunning level for stats purposes, regardless of a stale `dunning` field. */
const CLOSED_STATUSES = new Set<number>([
    InvoiceStatus.PAID,
    InvoiceStatus.WRITTEN_OFF,
    InvoiceStatus.DELETED,
]);

export function useDashboard(refreshTrigger = 0) {
    const { data: invoiceData, loading: loadingInvoices, error: invoiceError } = useApi<{ invoices: Invoice[] }>(
        "/invoices?minimal=1",
        refreshTrigger,
    );
    const { data: recipientsData, loading: loadingRecipients, error: recipientsError } = useApi<Recipient[]>(
        "/recipients",
        refreshTrigger,
    );
    const { data: questionsData } = useApi<{
        count: number;
        items: {
            id: string;
            recipientId: string;
            recipientName: string;
            category: string;
            invoiceId: string;
            invoiceReadableId: string;
        }[];
    }>("/dashboard/questions-count", refreshTrigger);

    const { data: questionnairePendingData } = useApi<{ count: number }>(
        "/dashboard/questionnaire-pending", refreshTrigger,
    );

    const loading = loadingInvoices || loadingRecipients;
    const loadError = invoiceError ?? recipientsError ?? null;

    const invoices = invoiceData?.invoices ?? [];
    const recipients = Array.isArray(recipientsData) ? recipientsData : [];
    const patientCount = recipients.reduce((sum, r) => {
        const patientList = (r.patients ?? []).filter(Boolean);
        return sum + (patientList.length > 1 ? patientList.length : 1);
    }, 0);
    const unansweredQuestions          = questionsData?.count ?? 0;
    const unansweredQuestionItems      = questionsData?.items ?? [];
    const questionnairePendingCount = questionnairePendingData?.count ?? 0;

    const totals = useMemo(() => {
        const closed = CLOSED_STATUSES;
        const calendarYear = new Date().getFullYear();
        /** Invoices excluded from “sent this year” gross (not open-balance logic). */
        const yearSentExcluded = new Set<number>([
            InvoiceStatus.DELETED,
            InvoiceStatus.WRITTEN_OFF,
        ]);

        // On the dashboard, any dunning level (M1/M2/M3/legal) counts as "overdue".
        const overdueCount = invoices.filter((i) => (i.dunning ?? 0) >= 1).length;

        const grossOf = (i: typeof invoices[number]) => {
            // Same fee + materials basis as InvoiceAmountsCard.tsx — omitting materialCosts here
            // silently undercounts any invoice that has them (all PAD-imported invoices with
            // billed materials), which is what made "Sent" fall out of sync with "Paid".
            const net = (i.amount ?? 0) + (i.materialCosts ?? 0);
            const vr  = i.vatRate ?? 0;
            return vr > 0 ? +(net * (1 + vr / 100)).toFixed(2) : net;
        };

        const remaining = (i: typeof invoices[number]) => {
            const gross = grossOf(i) + (i.dunningFineAmount ?? 0);
            return Math.max(0, gross - (i.paidAmount ?? 0));
        };

        // ── Hero KPIs ───────────────────────────────────────────────────────────
        // Total (open items): remaining balance on sent invoices that are not yet fully paid.
        const totalOutstanding = invoices
            .filter((i) => !!i.sentDate && !closed.has(i.status) && remaining(i) > 0.005)
            .reduce((s, i) => s + remaining(i), 0);

        // Sent (year): gross of all invoices sent in the calendar year (paid or unpaid); excl. deleted/cancelled/written-off.
        const amountSent = invoices
            .filter((i) => {
                if (!i.sentDate || yearSentExcluded.has(i.status)) return false;
                const y = new Date(i.sentDate).getFullYear();
                return y === calendarYear;
            })
            .reduce((s, i) => s + grossOf(i) + (i.dunningFineAmount ?? 0), 0);

        const amountPaid = invoices
            .filter((i) => i.status !== InvoiceStatus.DELETED)
            .reduce((s, i) => s + (i.paidAmount ?? 0), 0);

        const amountOverdue = invoices
            .filter((i) => !!i.sentDate && !closed.has(i.status) && (i.dunning ?? 0) >= 1)
            .reduce((s, i) => s + remaining(i), 0);

        // Stats (all time): gross amounts sent/paid + unsent remaining
        const unsentForSend = invoices.filter((i) => !closed.has(i.status) && !i.sentDate);
        const pendingToSendCount = unsentForSend.length;
        const amountPendingToSend = unsentForSend.reduce((s, i) => s + grossOf(i), 0);

        const amountM1    = invoices.filter((i) => !!i.sentDate && !closed.has(i.status) && i.dunning === 1).reduce((s, i) => s + remaining(i), 0);
        const amountM2    = invoices.filter((i) => !!i.sentDate && !closed.has(i.status) && i.dunning === 2).reduce((s, i) => s + remaining(i), 0);
        const amountM3    = invoices.filter((i) => !!i.sentDate && !closed.has(i.status) && i.dunning === 3).reduce((s, i) => s + remaining(i), 0);
        const amountLegal = invoices.filter((i) => !!i.sentDate && !closed.has(i.status) && (i.dunning ?? 0) >= 4).reduce((s, i) => s + remaining(i), 0);

        // Pie chart: sent (open, not in dunning) + dunning carry-overs (all time)
        const pieSent = invoices
            .filter((i) => !!i.sentDate && !closed.has(i.status) && (i.dunning ?? 0) === 0)
            .reduce((s, i) => s + remaining(i), 0);

        const overdueInvoices = invoices.filter((i) => !!i.sentDate && !closed.has(i.status) && (i.dunning ?? 0) >= 1);
        const overdueBreakdown = [1, 2, 3, 4].map((level) => {
            const lvl = overdueInvoices.filter((i) => level === 4 ? (i.dunning ?? 0) >= 4 : i.dunning === level);
            return {
                count: lvl.length,
                amount: lvl.reduce((s, i) => s + remaining(i), 0),
                fines: lvl.reduce((s, i) => s + (i.dunningFineAmount ?? 0), 0),
            };
        });
        const overdueTotalFines = overdueInvoices.reduce((s, i) => s + (i.dunningFineAmount ?? 0), 0);

        return {
            totalOutstanding,
            overdueCount,
            pendingToSendCount,
            amountPendingToSend,
            amountSent,
            amountPaid,
            amountM1,
            amountM2,
            amountM3,
            amountLegal,
            amountOverdue,
            pieSent,
            overdueBreakdown,
            overdueTotalFines,
        };
    }, [invoices]);

    const pendingInvoices = useMemo(() =>
        invoices
            .filter((i) => (i.amount ?? 0) + (i.materialCosts ?? 0) - (i.paidAmount ?? 0) > 2)
            .sort((a, b) => (b.dunning ?? 0) - (a.dunning ?? 0)),
    [invoices]);

    const openInvoiceCount = useMemo(() =>
        invoices.filter((i) =>
            !!i.sentDate &&
            i.status !== InvoiceStatus.PAID &&
            i.status !== InvoiceStatus.WRITTEN_OFF &&
            i.status !== InvoiceStatus.DELETED,
        ).length,
    [invoices]);

    const dunningBreakdown = useMemo(() => {
        // Only count invoices actually still open at that dunning level — a paid/written-off/
        // deleted invoice keeps its last `dunning` value forever, so without this filter a
        // long-settled invoice would still inflate that level's tile.
        const atLevel = (level: number) =>
            invoices.filter((i) => !!i.sentDate && !CLOSED_STATUSES.has(i.status) && i.dunning === level);

        const dunningCounts  = [0, 1, 2, 3, 4].map((level) => atLevel(level).length);
        const dunningAmounts = [0, 1, 2, 3, 4].map((level) =>
            atLevel(level)
                .reduce((s, i) => {
                    const net = (i.amount ?? 0) + (i.materialCosts ?? 0);
                    const vr = i.vatRate ?? 0;
                    const gross = vr > 0 ? +(net * (1 + vr / 100)).toFixed(2) : net;
                    return s + Math.max(0, gross + (i.dunningFineAmount ?? 0) - (i.paidAmount ?? 0));
                }, 0),
        );
        return { dunningCounts, dunningAmounts };
    }, [invoices]);

    const yearlyData = useMemo(() => {
        const year = new Date().getFullYear();
        return Array.from({ length: 12 }, (_, i) => {
            const label = new Date(year, i, 1).toLocaleString("de-DE", { month: "short" });
            const monthInvoices = invoices.filter((inv) => {
                const d = new Date(inv.creationDate);
                return d.getMonth() === i && d.getFullYear() === year;
            });
            const paid = monthInvoices.reduce((s, inv) => s + (inv.paidAmount ?? 0), 0);
            const open = monthInvoices.reduce((s, inv) => {
                const net = (inv.amount ?? 0) + (inv.materialCosts ?? 0);
                const vr = inv.vatRate ?? 0;
                const gross = vr > 0 ? +(net * (1 + vr / 100)).toFixed(2) : net;
                return s + Math.max(0, gross - (inv.paidAmount ?? 0));
            }, 0);
            return { label, open, paid };
        });
    }, [invoices]);

    const yearMonthlyData = useMemo(() => {
        const yearSentExcluded = new Set<number>([
            InvoiceStatus.DELETED,
            InvoiceStatus.WRITTEN_OFF,
        ]);
        const year = new Date().getFullYear();
        return Array.from({ length: 12 }, (_, i) => {
            const label = new Date(year, i, 1).toLocaleString("de-DE", { month: "short" });
            const monthInvoices = invoices.filter((inv) => {
                if (!inv.sentDate || yearSentExcluded.has(inv.status)) return false;
                const d = new Date(inv.sentDate);
                return d.getMonth() === i && d.getFullYear() === year;
            });
            const sent = monthInvoices.reduce((s, inv) => {
                const net = (inv.amount ?? 0) + (inv.materialCosts ?? 0);
                const vr = inv.vatRate ?? 0;
                const gross = vr > 0 ? +(net * (1 + vr / 100)).toFixed(2) : net;
                return s + gross + (inv.dunningFineAmount ?? 0);
            }, 0);
            const paid = monthInvoices.reduce((s, inv) => s + (inv.paidAmount ?? 0), 0);
            return { label, sent, paid };
        });
    }, [invoices]);

    const currentYear = new Date().getFullYear();

    return {
        loading,
        loadError,
        invoices,
        recipients,
        patientCount,
        unansweredQuestions,
        unansweredQuestionItems,
        questionnairePendingCount,
        ...totals,
        pendingInvoices,
        openInvoiceCount,
        ...dunningBreakdown,
        yearlyData,
        yearMonthlyData,
        currentYear,
        totalPending: totals.totalOutstanding,
    };
}
